/* eslint-disable react/forbid-prop-types */
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Typography, Avatar, alpha, TextField, InputAdornment, Tooltip, Stack,
    Autocomplete,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import { useSelector } from 'src/redux/store';
import { common } from 'src/theme/palette';
import { hasPractitionerRole } from '../../../utils/practiceUserRoles';

// ---- helpers (mirrors PracticeFieldsBlock.jsx) -----------------------------
const sortPractitioners = (a, b) => {
    const aFirst = (a?.user?.first_name || '').toLowerCase();
    const bFirst = (b?.user?.first_name || '').toLowerCase();
    if (aFirst !== bFirst) return aFirst.localeCompare(bFirst);
    const aLast = (a?.user?.last_name || '').toLowerCase();
    const bLast = (b?.user?.last_name || '').toLowerCase();
    return aLast.localeCompare(bLast);
};

const displayForPractitioner = (pu) => {
    const sal = (pu?.user?.salutation || '').trim();
    const fn = (pu?.user?.first_name || '').trim();
    const ln = (pu?.user?.last_name || '').trim();
    return [sal, fn, ln].filter(Boolean).join(' ');
};

const getInitials = (pu) => {
    const fn = pu?.user?.first_name || '';
    const ln = pu?.user?.last_name || '';
    return `${fn[0] || '?'}${ln[0] || ''}`.toUpperCase();
};

const QUICK_CHIP_MAX = 6;

/**
 * Step 2 — Treating doctor.
 *
 * Layout: one chip-row + one search dropdown — no inline expansion that
 * pushed the chips off-screen.
 *
 *   1. Chip quick-select — first QUICK_CHIP_MAX practitioners, plus a
 *      "+N more" placeholder when the practice has additional doctors.
 *      Always visible; this is the primary picker for small practices.
 *   2. Autocomplete search — the secondary picker for large practices.
 *      Filters the *full* in-memory practitioner list (Redux slice, no
 *      per-keystroke API call). Matches render in a floating popper —
 *      identical paper/row treatment to Step 1 and Step 3 so the
 *      affordance is consistent across the wizard.
 *
 * Writes to `practiceFieldValues['system:treated_by']` — same shape the
 * backend accepts.
 */
const QuickSendStep2Doctor = ({
    practiceFieldValues,
    setPracticeFieldValues,
}) => {
    const practiceUserResults = useSelector(
        (state) => state.practiceUser?.practiceUser?.results || [],
    );
    const currentUser = useSelector((state) => state.user?.user || null);

    // Autocomplete is uncontrolled for its `value` (we resolve selection
    // through Redux), but we drive `inputValue` so we can clear it on
    // pick — which closes the popper and surfaces the chip row again.
    const [inputValue, setInputValue] = useState('');

    const practitioners = useMemo(() => (
        practiceUserResults
            .filter((pu) => (
                hasPractitionerRole(pu)
                && pu?.user
                && pu.user.first_name
                && !pu.user.is_superuser
            ))
            .slice()
            .sort(sortPractitioners)
    ), [practiceUserResults]);

    const quickChips = useMemo(() => practitioners.slice(0, QUICK_CHIP_MAX), [practitioners]);

    const treatedByValue = practiceFieldValues?.['system:treated_by']?.value || '';

    // Seed the selection — prefer the current user if they're in the
    // practitioner list; fallback to the first practitioner.
    useEffect(() => {
        if (treatedByValue) return;
        if (!practitioners.length) return;
        const currentUserPractitioner = practitioners.find((pu) => (
            currentUser?.id
            && String(pu?.user?.id ?? pu?.id) === String(currentUser.id)
        ));
        const seed = currentUserPractitioner || practitioners[0];
        const userId = seed?.user?.id ?? seed?.id;
        setPracticeFieldValues({
            ...(practiceFieldValues || {}),
            'system:treated_by': {
                value: userId,
                display: displayForPractitioner(seed),
                source: `practice_user:${userId}`,
                visible_to_patient: false,
                label: 'Treated by',
            },
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practitioners, currentUser]);

    const handleSelect = (pu) => {
        if (!pu) return;
        const userId = pu?.user?.id ?? pu?.id;
        setPracticeFieldValues({
            ...(practiceFieldValues || {}),
            'system:treated_by': {
                value: userId,
                display: displayForPractitioner(pu),
                source: `practice_user:${userId}`,
                visible_to_patient: false,
                label: 'Treated by',
            },
        });
        // Clear the search after a selection — closes the popper and
        // brings the chip row back so the user sees their choice
        // confirmed in the chip with the checkmark badge.
        setInputValue('');
    };

    return (
        <Box>
            <Typography
                sx={{
                    fontSize: '10px', fontWeight: 800, letterSpacing: '0.08em',
                    color: common.mintyZest, textTransform: 'uppercase', mb: 0.5,
                }}
            >
                Step 2 · Treating doctor
            </Typography>
            <Typography sx={{ fontSize: '15px', fontWeight: 700, mb: 1.5 }}>
                Who is treating this patient?
            </Typography>

            {practitioners.length === 0 && (
                <Box
                    sx={{
                        p: 2, borderRadius: '10px',
                        bgcolor: 'rgba(145,158,171,0.06)',
                        border: '1px dashed', borderColor: 'divider',
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="caption" color="text.secondary">
                        No practitioners found for this practice.
                    </Typography>
                </Box>
            )}

            {/* Chip quick-select row — always visible. */}
            {quickChips.length > 0 && (
                <Stack
                    direction="row"
                    gap={1}
                    sx={{
                        mb: 1.5, pb: 0.5,
                        overflowX: 'auto', overflowY: 'hidden',
                        '&::-webkit-scrollbar': { height: 4 },
                        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(145,158,171,0.3)', borderRadius: 2 },
                    }}
                >
                    {quickChips.map((pu) => {
                        const userId = pu?.user?.id ?? pu?.id;
                        const isSelected = String(userId) === String(treatedByValue);
                        return (
                            <Tooltip key={userId} title={displayForPractitioner(pu)} enterDelay={400}>
                                <Box
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => handleSelect(pu)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleSelect(pu);
                                        }
                                    }}
                                    sx={{
                                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                                        gap: 0.5, p: 0.75, minWidth: 72, flexShrink: 0,
                                        borderRadius: '10px', cursor: 'pointer',
                                        bgcolor: isSelected ? alpha(common.mintyZest, 0.08) : 'transparent',
                                        border: '1px solid',
                                        borderColor: isSelected ? alpha(common.mintyZest, 0.32) : 'transparent',
                                        transition: 'background-color 120ms ease, border-color 120ms ease',
                                        '&:hover': { bgcolor: alpha(common.mintyZest, 0.06) },
                                    }}
                                >
                                    <Box sx={{ position: 'relative' }}>
                                        <Avatar
                                            sx={{
                                                width: 40, height: 40, fontSize: '13px', fontWeight: 800,
                                                bgcolor: isSelected ? common.mintyZest : alpha('#078DEE', 0.12),
                                                color: isSelected ? '#fff' : '#078DEE',
                                                border: '2px solid',
                                                borderColor: isSelected ? common.mintyZest : 'transparent',
                                            }}
                                        >
                                            {getInitials(pu)}
                                        </Avatar>
                                        {isSelected && (
                                            <Box
                                                sx={{
                                                    position: 'absolute', bottom: -2, right: -2,
                                                    width: 14, height: 14, borderRadius: '50%',
                                                    bgcolor: '#fff', display: 'flex',
                                                    alignItems: 'center', justifyContent: 'center',
                                                }}
                                            >
                                                <Iconify icon="eva:checkmark-circle-2-fill" width={14} sx={{ color: common.mintyZest }} />
                                            </Box>
                                        )}
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: '10.5px',
                                            fontWeight: isSelected ? 700 : 500,
                                            color: isSelected ? common.mintyZest : 'text.secondary',
                                            maxWidth: 64,
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {(pu?.user?.first_name || '').trim() || 'Dr'}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        );
                    })}
                    {practitioners.length > QUICK_CHIP_MAX && (
                        <Tooltip title={`${practitioners.length - QUICK_CHIP_MAX} more — type below to search`} enterDelay={300}>
                            <Box
                                sx={{
                                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                                    gap: 0.5, p: 0.75, minWidth: 72, flexShrink: 0,
                                    color: 'text.disabled',
                                }}
                            >
                                <Avatar
                                    sx={{
                                        width: 40, height: 40, fontSize: '11px', fontWeight: 700,
                                        bgcolor: 'rgba(145,158,171,0.08)', color: 'text.secondary',
                                        border: '1px dashed',
                                        borderColor: 'rgba(145,158,171,0.4)',
                                    }}
                                >
                                    +{practitioners.length - QUICK_CHIP_MAX}
                                </Avatar>
                                <Typography variant="caption" sx={{ fontSize: '10.5px' }}>more</Typography>
                            </Box>
                        </Tooltip>
                    )}
                </Stack>
            )}

            {/* Autocomplete search — floating popper, never expands inline.
                Matches Step 1's and Step 3's pattern so the dropdown
                affordance feels the same across the wizard. */}
            <Autocomplete
                fullWidth
                size="small"
                options={practitioners}
                value={null}
                inputValue={inputValue}
                onInputChange={(_, val, reason) => {
                    if (reason === 'input') setInputValue(val);
                    if (reason === 'clear') setInputValue('');
                }}
                onChange={(_, pu) => handleSelect(pu)}
                getOptionLabel={(pu) => (pu ? displayForPractitioner(pu) : '')}
                isOptionEqualToValue={(opt, val) => (
                    String(opt?.user?.id ?? opt?.id) === String(val?.user?.id ?? val?.id)
                )}
                filterOptions={(opts, state) => {
                    const q = state.inputValue.trim().toLowerCase();
                    if (!q) return opts;
                    return opts.filter((pu) => {
                        const display = displayForPractitioner(pu).toLowerCase();
                        const email = (pu?.user?.email || '').toLowerCase();
                        return display.includes(q) || email.includes(q);
                    });
                }}
                noOptionsText="No practitioners match."
                // Compact popper — about three full rows visible, the rest
                // scroll. ``mb`` on the listbox (NOT padding on the Paper —
                // MUI's internal styles override that) forces a clear 16px
                // white band between the last row and the Paper's rounded
                // bottom edge so the curve is unambiguously visible.
                // Mirrors QuickSendStep1Forms.jsx.
                ListboxProps={{
                    sx: {
                        maxHeight: 220,
                        pt: 0.5,
                        pb: 0,
                        mt: 0,
                        mb: 2,
                        scrollbarGutter: 'stable',
                        '&::-webkit-scrollbar': { width: 6 },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': {
                            background: 'rgba(145,158,171,0.32)',
                            borderRadius: 6,
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: 'rgba(145,158,171,0.48)',
                        },
                    },
                }}
                // Flip upward when the trigger sits low in the dialog so the
                // dropdown stays inside the dialog bounds. ``preventOverflow``
                // keeps it from being nudged off-screen on tight viewports.
                //
                // CRITICAL: `src/App.css` ships a global rule
                // `.MuiAutocomplete-popper { background: #fff }` which renders
                // an unrounded white rectangle BEHIND the Paper — that's the
                // square-corner leak at the dropdown's bottom edge. We force
                // the Popper's background back to transparent here (sx beats
                // the global class selector on specificity) so only the Paper
                // is visible. Same fix as QuickSendStep1Forms.jsx.
                slotProps={{
                    popper: {
                        sx: {
                            background: 'transparent !important',
                            backgroundColor: 'transparent !important',
                        },
                        modifiers: [
                            { name: 'flip', enabled: true },
                            { name: 'preventOverflow', enabled: true, options: { altAxis: true, padding: 8 } },
                        ],
                    },
                }}
                componentsProps={{
                    paper: {
                        sx: {
                            mt: 0.75,
                            // Strong 18px curve so the rounded bottom corners
                            // are immediately obvious, not subtle.
                            borderRadius: '18px',
                            boxShadow: '0 20px 48px rgba(0,0,0,0.16), 0 2px 10px rgba(0,0,0,0.05)',
                            border: '1px solid',
                            borderColor: 'rgba(145,158,171,0.2)',
                            overflow: 'hidden',
                            bgcolor: '#fff',
                            // Hard-override any MUI default padding on the
                            // inner listbox so our ``mb: 2`` wins and the
                            // bottom white band is always present.
                            '& .MuiAutocomplete-listbox': {
                                marginBottom: '16px !important',
                            },
                            // The last visible row's hairline borderBottom can
                            // otherwise pretend to be the dropdown's bottom
                            // edge — strip it so only the Paper's rounded
                            // curve reads as the end.
                            '& .MuiAutocomplete-listbox > li:last-of-type, & .MuiAutocomplete-listbox > li > ul > li:last-of-type': {
                                borderBottom: 'none',
                            },
                        },
                    },
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={
                            practitioners.length > QUICK_CHIP_MAX
                                ? `Search all ${practitioners.length} practitioners…`
                                : 'Search practitioners…'
                        }
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Iconify icon="eva:search-fill" width={16} sx={{ color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '13px', bgcolor: '#fff' },
                        }}
                    />
                )}
                renderOption={(props, pu) => {
                    const userId = pu?.user?.id ?? pu?.id;
                    const isSelected = String(userId) === String(treatedByValue);
                    return (
                        <Box
                            component="li"
                            {...props}
                            key={userId}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 1.25,
                                py: 1, px: 1.5,
                                borderBottom: '1px solid',
                                borderColor: 'rgba(145,158,171,0.08)',
                                transition: 'background-color 100ms ease',
                                '&:last-of-type': { borderBottom: 'none' },
                                '&.Mui-focused, &:hover': {
                                    bgcolor: `${alpha(common.mintyZest, 0.06)} !important`,
                                },
                            }}
                        >
                            <Avatar
                                sx={{
                                    width: 32, height: 32, fontSize: '11.5px', fontWeight: 800,
                                    bgcolor: isSelected ? common.mintyZest : alpha('#078DEE', 0.12),
                                    color: isSelected ? '#fff' : '#078DEE',
                                }}
                            >
                                {getInitials(pu)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 600, fontSize: '13px', lineHeight: 1.3 }} noWrap>
                                    {displayForPractitioner(pu)}
                                </Typography>
                                {pu?.user?.email && (
                                    <Typography
                                        variant="caption"
                                        sx={{ color: 'text.disabled', fontSize: '11px' }}
                                        noWrap
                                    >
                                        {pu.user.email}
                                    </Typography>
                                )}
                            </Box>
                            {isSelected && (
                                <Iconify icon="eva:checkmark-circle-2-fill" width={18} sx={{ color: common.mintyZest }} />
                            )}
                        </Box>
                    );
                }}
            />
        </Box>
    );
};

QuickSendStep2Doctor.propTypes = {
    practiceFieldValues: PropTypes.object,
    setPracticeFieldValues: PropTypes.func.isRequired,
};

QuickSendStep2Doctor.defaultProps = {
    practiceFieldValues: {},
};

export default QuickSendStep2Doctor;
